import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, ClickAwayListener, Collapse, Divider, Grow, IconButton, ListItemText, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { LanguageEnum } from 'gleblu-socket-api';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { GUEST_BOOK_MENU_ITEM_ID, SETTINGS_MENU_ITEM_ID, SIGN_OUT_MENU_ITEM_ID } from '@/constants/testIds';

import { PlaceIcon } from '../../components/icons/icons';
import PlaceContextData from '../../components/placeContext/PlaceContextData';
import Typography from '../../components/typography/Typography';
import { GUESTBOOK_URL, LOGIN_URL, MANAGEMENT_URL, PLACES, SETTINGS_URL } from '../../constants/RouterConstants';
import supportedLanguages from '../../localization/localizations/supportedLanguages';
import Translation from '../../localization/translations/Translation';
import LoggedInUser from '../../models/interfaces/LoggedInUser';
import { logout } from '../../store/actions/authentication/logout';
import { languageLocaleChanged } from '../../store/actions/language/languageLocaleChanged';
import { selectedLanguageSelector } from '../../store/selectors/languageSelectors';
import PlaceSelectionMenu from './PlaceSelectionMenu';

const useStyles = makeStyles(() =>
  createStyles({
    paper: {
      minWidth: '260px',
    },
    languageMenuItem: {
      height: 'auto',
    },
    popover: {
      left: '-80px',
      zIndex: 30,
    },
  })
);

interface Props {
  place: PlaceContextData | null;
  translatedStrings: Translation;
  loggedInUser: LoggedInUser | null;
  onShowFeedbackDialog: (callback: () => void) => void;
}

interface OpenStates {
  language: boolean;
  selectedPlace: boolean;
}

const NavigationMenu = (props: Props) => {
  const { translatedStrings, place, loggedInUser, onShowFeedbackDialog } = props;

  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [nestedOpens, setNestedOpens] = useState<OpenStates>({ language: false, selectedPlace: false });

  const selectedLanguage = useSelector(selectedLanguageSelector);

  const handleNested = (nestedMenu: keyof OpenStates) => {
    setNestedOpens((prev) => ({ ...prev, [nestedMenu]: !prev[nestedMenu] }));
  };

  const closeNested = (nestedMenu: keyof OpenStates) => {
    setNestedOpens((prev) => ({ ...prev, [nestedMenu]: false }));
  };

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleClose = () => {
    setOpen(false);
    setNestedOpens({ language: false, selectedPlace: false });
  };

  const handleClickAway = (event) => {
    if (anchorEl?.contains(event.target)) {
      return;
    }

    setOpen(false);
    setNestedOpens({ language: false, selectedPlace: false });
  };

  const handleNavigation = (url) => {
    history.push(url);
  };

  const handleLanguageChange = (languageEnum: LanguageEnum) => {
    dispatch(languageLocaleChanged({ languageEnum }));
    closeNested('language');
  };

  const handleLogout = () => {
    history.push(LOGIN_URL);
    dispatch(logout());
  };

  return (
    <div>
      <IconButton ref={setAnchorEl} size="large" aria-owns={open ? 'simple-menu' : undefined} aria-haspopup="true" onClick={handleToggle}>
        <PlaceIcon />
      </IconButton>
      <Popper open={open} anchorEl={anchorEl} transition className={classes.popover}>
        {({ TransitionProps, placement }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
            <Paper className={classes.paper}>
              <ClickAwayListener onClickAway={handleClickAway}>
                <MenuList>
                  {loggedInUser && [
                    <MenuItem key={loggedInUser.name} disabled>
                      <ListItemText disableTypography primary={loggedInUser.name} />
                    </MenuItem>,
                  ]}
                  {place &&
                    loggedInUser && [
                      <Divider key="divider3" />,
                      <MenuItem key={MANAGEMENT_URL} onClick={() => handleNavigation(`${PLACES}/${place.uniqueName}${MANAGEMENT_URL}`)}>
                        <ListItemText disableTypography primary={translatedStrings.reservationManagementMenuText} />
                      </MenuItem>,
                      <MenuItem
                        data-id={SETTINGS_MENU_ITEM_ID}
                        key={SETTINGS_URL}
                        onClick={() => handleNavigation(`${PLACES}/${place.uniqueName}${SETTINGS_URL}`)}
                      >
                        <ListItemText disableTypography primary={translatedStrings.settingsPageManagementMenuText} />
                      </MenuItem>,
                      // <MenuItem
                      //   key={RESERVATIONS_ANALYTICS_URL}
                      //   onClick={() => this.handleNavigation(`${PLACES}/${place.uniqueName}${RESERVATIONS_ANALYTICS_URL}`)}
                      // >
                      //   <ListItemText disableTypography primary={translatedStrings.reservationsAnalyticsPageManagementMenuText} />
                      // </MenuItem>, TODO 481: Analytics
                      // <MenuItem key={GUESTS_ANALYTICS_URL} onClick={() => this.handleNavigation(`${PLACES}/${place.uniqueName}${GUESTS_ANALYTICS_URL}`)}>
                      //   <ListItemText disableTypography primary={translatedStrings.guestsAnalyticsPageManagementMenuText} />
                      // </MenuItem>, TODO 481: Analytics
                      <Divider key="divider1" />,

                      <MenuItem
                        data-id={GUEST_BOOK_MENU_ITEM_ID}
                        key={GUESTBOOK_URL}
                        onClick={() => handleNavigation(`${PLACES}/${place.uniqueName}${GUESTBOOK_URL}`)}
                      >
                        <ListItemText disableTypography primary={translatedStrings.guestbook} />
                      </MenuItem>,
                      <Divider key="divider2" />,
                    ]}
                  {loggedInUser && [
                    <PlaceSelectionMenu key={PLACES} open={nestedOpens.selectedPlace} toggleOpen={() => handleNested('selectedPlace')} />,
                    <Divider key="divider1" />,
                  ]}
                  <MenuItem onClick={() => handleNested('language')} className={classes.languageMenuItem}>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography variant="subtitle1" color="inherit">
                          {translatedStrings.languageMenuText}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="inherit">
                          {supportedLanguages[selectedLanguage].displayName}
                        </Typography>
                      }
                    />
                    {nestedOpens.language ? <ExpandLess /> : <ExpandMore />}
                  </MenuItem>
                  <Collapse in={nestedOpens.language} timeout="auto" unmountOnExit>
                    <MenuList disablePadding>
                      {Object.values(supportedLanguages).map(({ displayName, languageEnum, locale }) => (
                        <MenuItem key={locale} onClick={() => handleLanguageChange(languageEnum)} selected={languageEnum === selectedLanguage}>
                          <ListItemText disableTypography inset primary={displayName} />
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Collapse>

                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    <Divider key="divider3" />
                    <MenuItem onClick={() => onShowFeedbackDialog(handleClose)}>
                      <ListItemText disableTypography primary={translatedStrings.sendFeedbackCallToAction} />
                    </MenuItem>
                  </Box>

                  {!loggedInUser && [
                    <Divider key="divider3" />,
                    <MenuItem
                      key={LOGIN_URL}
                      onClick={() => {
                        history.push(LOGIN_URL);
                      }}
                    >
                      <ListItemText disableTypography primary={translatedStrings.login} />
                    </MenuItem>,
                  ]}

                  {loggedInUser && [
                    <Divider key="divider3" />,
                    <MenuItem data-id={SIGN_OUT_MENU_ITEM_ID} key={LOGIN_URL} onClick={handleLogout}>
                      <ListItemText disableTypography primary={translatedStrings.signOutMenuText} />
                    </MenuItem>,
                  ]}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default NavigationMenu;
